import { useEffect, useRef } from "react";
import { NotificationManager } from "react-notifications";

/**
 * Watches the stored session's tokenExpiration and shows a top-right toast
 * as the deadline approaches. Each threshold fires exactly once per session
 * (identified by the exact expiration timestamp), so a new login resets it
 * and back-to-back renders don't spam.
 *
 * Non-blocking by design — toasts are small, top-right, and never modal.
 *
 * We keep the shown-threshold set in localStorage so multiple browser tabs
 * agree on what's already been shown (no duplicate toasts across tabs).
 */

const STORAGE_KEY = "loggedInUser";
const SHOWN_KEY = "sessionExpiryWarningsShown";
const CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds — cheap, survives tab sleep

// Ordered largest → smallest so a fresh login that's already inside a
// threshold band still fires the correct (most urgent) toast.
const THRESHOLDS = [
    { minutes: 15, level: "info", title: "Session Ending Soon", timeout: 6000 },
    { minutes: 5, level: "warning", title: "Session Expiring Soon", timeout: 0 },
    { minutes: 1, level: "error", title: "Session Expiring Now", timeout: 0 },
];

function readExpiration() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.tokenExpiration) return null;
        const exp = Date.parse(parsed.tokenExpiration);
        return Number.isFinite(exp) ? exp : null;
    } catch {
        return null;
    }
}

function readShownState() {
    try {
        const raw = localStorage.getItem(SHOWN_KEY);
        return raw ? JSON.parse(raw) : { exp: null, shown: [] };
    } catch {
        return { exp: null, shown: [] };
    }
}

function writeShownState(state) {
    try {
        localStorage.setItem(SHOWN_KEY, JSON.stringify(state));
    } catch {
        // localStorage full / disabled — swallow, we'd rather miss a toast than crash
    }
}

function showToast(threshold, minutesLeft) {
    const rounded = Math.max(1, Math.round(minutesLeft));
    const body =
        threshold.minutes === 1
            ? `Your session expires in under a minute. Save your work now to avoid losing changes.`
            : `Your session expires in about ${rounded} minute${rounded !== 1 ? "s" : ""}. Save your work soon.`;

    // Call on the object directly — these methods rely on internal `this` binding.
    if (threshold.level === "error") {
        NotificationManager.error(body, threshold.title, threshold.timeout);
    } else if (threshold.level === "warning") {
        NotificationManager.warning(body, threshold.title, threshold.timeout);
    } else {
        NotificationManager.info(body, threshold.title, threshold.timeout);
    }
}

export default function useSessionExpiryWarnings() {
    const intervalRef = useRef(null);

    useEffect(() => {
        const check = () => {
            const exp = readExpiration();
            if (!exp) return; // logged out or malformed session — nothing to warn about

            const now = Date.now();
            if (exp <= now) return; // already expired — the existing redirect handles it

            // If the expiration timestamp changed (new login), reset the shown set.
            let state = readShownState();
            if (state.exp !== exp) {
                state = { exp, shown: [] };
                writeShownState(state);
            }

            const minutesLeft = (exp - now) / 60000;

            // Fire the most urgent unfired threshold we've already crossed.
            for (const threshold of THRESHOLDS) {
                if (minutesLeft <= threshold.minutes && !state.shown.includes(threshold.minutes)) {
                    showToast(threshold, minutesLeft);
                    state.shown.push(threshold.minutes);
                    writeShownState(state);
                    break; // one toast per tick — don't cascade
                }
            }
        };

        // Check immediately on mount (covers the case where a user refreshed the
        // page 6 minutes before expiry), then on an interval.
        check();
        intervalRef.current = setInterval(check, CHECK_INTERVAL_MS);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);
}
